import { useState } from 'react'
import { toast } from 'sonner'
import {
  useProductStageRequirements,
  useCreateProductRequirement,
  useDeleteRequirement,
} from '@/features/contract/gating/hooks'
import { useProductFormSchema } from '@/features/contract/elements/hooks'

function formatError(err: unknown): string {
  const ax = err as { response?: { data?: { detail?: string; title?: string } } }
  return ax.response?.data?.detail ?? ax.response?.data?.title ?? 'Błąd zapisu wymagania'
}

/**
 * Element-stage gating editor (FIELD only): defines which element fields must be filled
 * before the element can leave this stage. Groups are OR between, AND within.
 */
export function ProductStageGatingPanel({ productCode, stageCode }: { productCode: string; stageCode: string }) {
  const reqsQ = useProductStageRequirements(productCode, stageCode)
  const schemaQ = useProductFormSchema(productCode)
  const create = useCreateProductRequirement(productCode)
  const del = useDeleteRequirement()
  const [fieldCode, setFieldCode] = useState('')
  const [groupNo, setGroupNo] = useState(1)

  const reqs = reqsQ.data ?? []
  const fields = schemaQ.data?.fields ?? []
  const fieldLabel = (code: string | null) =>
    fields.find(f => f.fieldCode === code)?.labelPl ?? code ?? '?'

  const groups = Array.from(new Set(reqs.map(r => r.groupNo))).sort((a, b) => a - b)
  const maxGroup = groups.length ? Math.max(...groups) : 0

  const add = () => {
    if (!fieldCode) return
    create.mutate(
      { kind: 'FIELD', stageCode, fieldCode, groupNo, sortOrder: 0 },
      { onSuccess: () => setFieldCode(''), onError: e => toast.error(formatError(e)) },
    )
  }

  return (
    <div className="flex flex-col gap-3 text-sm">
      <p className="text-xs text-slate-500">
        Element nie przejdzie z tego etapu, póki wskazane pola elementu nie są wypełnione.
        Grupy = <strong>LUB</strong> między sobą, pola w grupie = <strong>I</strong>.
      </p>

      {groups.length === 0 && (
        <p className="text-xs text-slate-400 italic">Brak wymagań - przejście jest otwarte.</p>
      )}

      {groups.map(g => (
        <div key={g} className="border rounded p-2">
          <div className="text-[11px] uppercase text-slate-400 mb-1">Grupa {g} (wszystkie poniższe)</div>
          <ul className="flex flex-col gap-1">
            {reqs.filter(r => r.groupNo === g).map(r => (
              <li key={r.id} className="flex items-center justify-between gap-2">
                <span>
                  Pole <strong>{fieldLabel(r.fieldCode)}</strong>{' '}
                  <span className="font-mono text-[10px] text-slate-400">{r.fieldCode}</span> wypełnione
                </span>
                <button
                  type="button"
                  onClick={() => del.mutate(r.id, { onError: e => toast.error(formatError(e)) })}
                  className="text-xs text-red-600"
                >Usuń</button>
              </li>
            ))}
          </ul>
        </div>
      ))}

      <div className="flex items-end gap-2 border-t pt-3">
        <label className="grid gap-1">
          <span className="text-[11px] uppercase text-slate-500">Pole elementu</span>
          <select
            value={fieldCode}
            onChange={e => setFieldCode(e.target.value)}
            className="border rounded px-2 py-1 text-sm min-w-48"
          >
            <option value="">- wybierz pole -</option>
            {fields.map(f => (
              <option key={f.fieldCode} value={f.fieldCode}>{f.labelPl} ({f.fieldCode})</option>
            ))}
          </select>
        </label>
        <label className="grid gap-1">
          <span className="text-[11px] uppercase text-slate-500">Grupa</span>
          <select
            value={groupNo}
            onChange={e => setGroupNo(Number(e.target.value))}
            className="border rounded px-2 py-1 text-sm"
          >
            {groups.map(g => <option key={g} value={g}>Grupa {g}</option>)}
            <option value={maxGroup + 1}>+ nowa grupa (LUB)</option>
          </select>
        </label>
        <button
          type="button"
          onClick={add}
          disabled={!fieldCode || create.isPending}
          className="px-3 py-1.5 bg-sky-600 text-white rounded text-sm disabled:opacity-50"
        >
          Dodaj wymaganie
        </button>
      </div>

      {fields.length === 0 && (
        <p className="text-[11px] text-amber-600">
          Ten produkt nie ma jeszcze pól - dodaj je w zakładce „Sekcje formularza" / Form Builderze.
        </p>
      )}
    </div>
  )
}
